import { motion } from "motion/react";
import { useState } from "react";
import * as LucideIcons from "lucide-react";
import { ArrowRight } from "lucide-react";
import type { Suggestion } from "../../types";

interface StackedSuggestionsProps {
  suggestions?: Suggestion[];
  onSelect?: (suggestion: string, prefillOnly?: boolean) => void;
}

/**
 * Default fallback icons for suggestions when no icon is provided
 * Used in round-robin fashion by suggestion index
 */
const FALLBACK_ICONS = ["Sparkles", "Brain", "Lightbulb", "Zap", "MessageSquare", "TrendingUp"];

/**
 * Helper function to resolve lucide icon names to components
 * Supports icon names like "Sparkles", "Zap", "MessageSquare", etc.
 */
const getLucideIcon = (iconName: string | undefined, fallbackIndex?: number) => {
  let nameToResolve = iconName;
  
  // Use fallback icon if no name provided
  if (!nameToResolve && fallbackIndex !== undefined) {
    nameToResolve = FALLBACK_ICONS[fallbackIndex % FALLBACK_ICONS.length];
  }
  
  if (!nameToResolve || typeof nameToResolve !== 'string') return null;
  
  // Try to find the icon in lucide-react
  const IconComponent = (LucideIcons)[nameToResolve];
  if (IconComponent) {
    return <IconComponent className="w-4 h-4" />;
  }
  
  // Fallback to rendering as text (emoji or string)
  return <span className="text-base">{nameToResolve}</span>;
};

export const StackedSuggestions = ({ 
  suggestions = [], 
  onSelect 
}: StackedSuggestionsProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative mb-3"
      style={{ height: `${suggestions.length * 36 + 12}px` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {suggestions.map((suggestion, index) => {
        return (
          <motion.button
            key={suggestion.id}
            className="absolute left-0 right-0 w-full bg-card border border-border rounded-lg px-3 py-2 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex items-center gap-2 text-left hover:bg-accent/5 group"
            initial={{ y: -index * 8, scale: 1 - index * 0.02 }}
            animate={{
              y: isHovered ? -index * 48 : -index * 8,
              scale: isHovered ? 1 : 1 - index * 0.02,
              opacity: isHovered ? 1 : 1 - index * 0.1,
            }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 40,
              mass: 0.8,
            }}
            onClick={() => onSelect?.(suggestion.text, suggestion.prefillOnly)}
            style={{ 
              zIndex: suggestions.length - index,
              bottom: 0,
            }}
          >
            <div className="flex items-center justify-center w-6 h-6 rounded-md bg-accent/10 text-accent-foreground flex-shrink-0">
              {typeof suggestion.icon === 'string' ? (
                getLucideIcon(suggestion.icon, index)
              ) : (
                suggestion.icon || getLucideIcon(undefined, index)
              )}
            </div>
            <span className="text-xs font-medium text-foreground flex-1 line-clamp-1">
              {suggestion.text}
            </span>
            {/* Arrow indicator on hover */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <ArrowRight className="w-4 h-4 text-custom-text-secondary" />
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};
